import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Columns3 } from 'lucide-react';

export type LocationColumnKey =
  | 'image'
  | 'name'
  | 'site'
  | 'account'
  | 'coordinates'
  | 'transmitters'
  | 'userGroup'
  | 'sensors'
  | 'tags'
  | 'status';

export const DEFAULT_VISIBLE_COLUMNS: LocationColumnKey[] = [
  'image',
  'name',
  'site',
  'account',
  'coordinates',
  'transmitters',
  'userGroup',
  'sensors',
  'tags',
  'status',
];

const COLUMN_LABELS: Record<LocationColumnKey, string> = {
  image: 'table.image',
  name: 'table.name',
  site: 'table.site',
  account: 'table.account',
  coordinates: 'table.coordinates',
  transmitters: 'table.transmitters',
  userGroup: 'table.userGroup',
  sensors: 'table.sensors',
  tags: 'table.tags',
  status: 'table.status',
};

interface ColumnVisibilityToggleProps {
  visibleColumns: LocationColumnKey[];
  onVisibleColumnsChange: (columns: LocationColumnKey[]) => void;
}

export function ColumnVisibilityToggle({
  visibleColumns,
  onVisibleColumnsChange,
}: ColumnVisibilityToggleProps) {
  const { t } = useTranslation('locations');

  const toggleColumn = (col: LocationColumnKey) => {
    if (visibleColumns.includes(col)) {
      // Don't allow hiding all columns - keep at least 'name'
      if (col === 'name') return;
      onVisibleColumnsChange(visibleColumns.filter((c) => c !== col));
    } else {
      onVisibleColumnsChange([...visibleColumns, col]);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Columns3 className="h-4 w-4 mr-1" />
          {t('toolbar.columns', 'Columns')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {DEFAULT_VISIBLE_COLUMNS.map((col) => (
          <DropdownMenuCheckboxItem
            key={col}
            checked={visibleColumns.includes(col)}
            onCheckedChange={() => toggleColumn(col)}
            disabled={col === 'name'} // Name is always visible
          >
            {t(COLUMN_LABELS[col], col)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
